import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, ChevronRight, Laptop } from "lucide-react";
import {
  EmptyState,
  KindBadge,
  ProgressBar,
  RUN_BUTTON,
  SectionCard,
  ShowMore,
} from "@/components/new-look/blocks";
import { MarkdownRenderer } from "@/components/common/markdown-renderer";

/** Text longer than this folds behind "Show more". */
const LONG_TEXT = 600;
import { countOf } from "@/components/new-look/labels";
import { NEEDS_YOU_TEXT, type IdeaStageResult } from "@/components/new-look/stage";
import { RunOnMachineDialog } from "@/components/epic-requests/run-on-machine-dialog";
import { BuildDecision } from "@/components/new-look/build-decision";
import { WaitingPullRequests } from "@/components/mailbox/waiting-pull-requests";
import { useBlueprint } from "@/hooks/queries/use-epics";
import { useFeatures } from "@/hooks/queries/use-features";
import { useTasks } from "@/hooks/queries/use-tasks";
import {
  OWNER_TURN_BODY,
  OwnerChecks,
  isOwnerTurn,
  ownerTurnTitle,
  partsDoneText,
  useOwnerChecks,
} from "@/components/new-look/owner-checks";
import { useDecisions } from "@/hooks/queries/use-decisions";
import { useJob } from "@/hooks/queries/use-mailbox";
import { usePageTitle } from "@/hooks/use-page-title";
import type { Epic, Feature, Status, StructuredDescription } from "@/lib/api/types";
import type { Decision } from "@/lib/api/decisions";
import { cn } from "@/lib/utils";
import { updatedAgo } from "../ideas/idea-helpers";
import { BLUEPRINT_STATUS, blueprintStage } from "./all-blueprints";

/**
 * One blueprint's page (docs/new-look/PLAN.md, step 5.2): what it builds, its
 * parts and their tasks, what "done" means for each part, the decisions made
 * along the way, its pull requests, and the one next thing to do (start
 * building, watch the build, review). Everything else stays on the classic page.
 */

/** A feature's structured description, from the JSON text the list returns. */
export function parseStructuredDesc(raw: Feature["structuredDesc"]): StructuredDescription | null {
  if (!raw) return null;
  if (typeof raw !== "string") return raw;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as StructuredDescription) : null;
  } catch {
    return null;
  }
}

const WORK_STATE: Record<Status["category"], { label: string; chip: string }> = {
  backlog: { label: "Not started", chip: "bg-muted text-muted-foreground" },
  unstarted: { label: "Not started", chip: "bg-muted text-muted-foreground" },
  started: {
    label: "In progress",
    chip: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-200",
  },
  completed: {
    label: "Done",
    chip: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200",
  },
  canceled: { label: "Dropped", chip: "bg-muted text-muted-foreground line-through" },
};

function workState(status: Status | undefined) {
  return WORK_STATE[status?.category ?? "unstarted"];
}

interface BlueprintNext {
  title: string;
  body: string;
  to: string | null;
  action: string | null;
  urgent: boolean;
  /** Start the build from here with today's send-to-machine. */
  build?: boolean;
  /** Decide the build here: mark it done or say what's missing. */
  decide?: boolean;
}

/** The one thing to do next on a blueprint, and where to do it. */
export function blueprintNextStep(epic: Epic, result: IdeaStageResult): BlueprintNext {
  const facts = epic.facts;
  const buildRun = facts?.buildJob ? `/mailbox/jobs/${facts.buildJob.id}` : null;
  const planRun = facts?.planJob ? `/mailbox/jobs/${facts.planJob.id}` : null;
  const classic = `/epics/${epic.id}`;

  switch (result.needsYou) {
    case "answer_blueprint_maker":
      return {
        title: NEEDS_YOU_TEXT.answer_blueprint_maker,
        body: "Planning is paused on a question.",
        to: planRun ?? classic,
        action: "Answer",
        urgent: true,
      };
    case "answer_builder":
      return {
        title: NEEDS_YOU_TEXT.answer_builder,
        body: "The build is paused on a question.",
        to: buildRun ?? classic,
        action: "Answer",
        urgent: true,
      };
    case "merge_prs":
      return {
        title: NEEDS_YOU_TEXT.merge_prs,
        body: "The build's pull requests are ready for you, below.",
        to: buildRun,
        action: buildRun ? "Open the build" : null,
        urgent: true,
      };
    case "decide_build":
      return {
        title: NEEDS_YOU_TEXT.decide_build,
        body: "The final check wasn't sure the build is complete. Read what it found, then decide.",
        to: null,
        action: null,
        urgent: true,
        decide: true,
      };
    case "review_build":
      return {
        title: NEEDS_YOU_TEXT.review_build,
        body: "The build is done and checked. Review it and merge.",
        to: epic.buildPullRequest?.url ?? classic,
        action: "Review",
        urgent: true,
      };
    default:
      break;
  }
  if (result.stage === "done") {
    return {
      title: "Shipped",
      body: "Built, checked and merged.",
      to: null,
      action: null,
      urgent: false,
    };
  }
  if (planRun) {
    return {
      title: "The Blueprint maker is working",
      body: "It's still adding parts and tasks.",
      to: planRun,
      action: "Watch",
      urgent: false,
    };
  }
  if (buildRun) {
    return {
      title: "Building",
      body: "The Builder is working on it now.",
      to: buildRun,
      action: "Watch the build",
      urgent: false,
    };
  }
  if (result.stage === "build") {
    return {
      title: "Partly built",
      body: "Some parts are done. Start building again to carry on.",
      to: null,
      action: null,
      urgent: false,
      build: true,
    };
  }
  return {
    title: "Ready to build",
    body: "Check the parts and what done means, then start building.",
    to: null,
    action: null,
    urgent: false,
    build: true,
  };
}

function DoneMeans({ criteria }: { criteria: string[] }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Done means
      </h4>
      <ul className="mt-1.5 flex flex-col gap-1">
        {criteria.map((c, i) => (
          <li key={i} className="flex gap-2 text-sm text-foreground">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="min-w-0">{c}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A part's tasks, loaded when the part is opened. */
function PartTasks({ featureId }: { featureId: string }) {
  const { data, isLoading, isError } = useTasks({ featureId, limit: 100 });
  const tasks = data?.pages.flatMap((p) => p.data) ?? [];
  if (isLoading) return <div className="h-16 animate-pulse rounded-lg bg-muted" />;
  if (isError) return <p className="text-sm text-muted-foreground">Couldn't load the tasks.</p>;
  if (tasks.length === 0) return <p className="text-sm text-muted-foreground">No tasks yet.</p>;
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tasks</h4>
      <ol className="mt-1.5 flex flex-col">
        {tasks.map((t) => {
          const state = workState(t.status);
          return (
            <li key={t.id}>
              <Link
                to={`/tasks/${t.id}`}
                className="flex min-h-11 items-center gap-3 rounded-lg px-2 hover:bg-muted"
              >
                <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                  {t.identifier}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-foreground">{t.title}</span>
                <span
                  className={cn(
                    "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                    state.chip
                  )}
                >
                  {state.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Part({ feature, index }: { feature: Feature; index: number }) {
  const [open, setOpen] = useState(false);
  const desc = parseStructuredDesc(feature.structuredDesc);
  const state = workState(feature.status);
  const total = feature._count?.tasks ?? 0;
  const done = feature.completedTaskCount ?? 0;
  const criteria = desc?.acceptanceCriteria?.filter((c) => c.trim()) ?? [];
  return (
    <li className="border-b border-border last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
        }}
        className="flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none sm:px-5"
      >
        <ChevronRight
          aria-hidden="true"
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90"
          )}
        />
        <span className="w-6 shrink-0 text-sm tabular-nums text-muted-foreground">{index + 1}</span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[15px] font-semibold text-foreground">
            {feature.title}
          </span>
          <span className="text-xs text-muted-foreground">
            {[
              feature.identifier,
              total > 0 ? `${String(done)} of ${countOf(total, "task")} done` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </span>
        <span
          className={cn("shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold", state.chip)}
        >
          {state.label}
        </span>
      </button>
      {open && (
        <div className="flex flex-col gap-4 px-4 pb-4 pl-[3.25rem] sm:px-5 sm:pl-[3.75rem]">
          {desc?.summary.trim() && (
            <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/90">
              {desc.summary}
            </p>
          )}
          {criteria.length > 0 && <DoneMeans criteria={criteria} />}
          <PartTasks featureId={feature.id} />
          <Link
            to={`/features/${feature.id}`}
            className="self-start text-sm text-primary hover:underline"
          >
            Open this part
          </Link>
        </div>
      )}
    </li>
  );
}

function Parts({ epicId }: { epicId: string }) {
  const { data, isLoading, isError } = useFeatures({ epicId, limit: 100 });
  const features = data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <section
      aria-label="Parts"
      className="overflow-hidden rounded-2xl border border-border bg-background"
    >
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5">
        <h2 className="text-base font-semibold text-foreground">Parts</h2>
        {features.length > 0 && (
          <span className="text-sm text-muted-foreground">{countOf(features.length, "part")}</span>
        )}
      </div>
      {isLoading ? (
        <div aria-busy="true" aria-label="Loading parts" className="flex flex-col gap-2 p-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : isError ? (
        <p className="p-5 text-sm text-muted-foreground">Couldn't load the parts.</p>
      ) : features.length === 0 ? (
        <p className="p-5 text-sm text-muted-foreground">
          No parts yet. The Blueprint maker adds them.
        </p>
      ) : (
        <ol>
          {features.map((f, i) => (
            <Part key={f.id} feature={f} index={i} />
          ))}
        </ol>
      )}
    </section>
  );
}

const DECISIONS_SHOWN = 5;

function DecisionItem({ d }: { d: Decision }) {
  return (
    <li className="flex flex-col gap-1 border-b border-border py-3 first:pt-0 last:border-b-0 last:pb-0">
      <span className="text-sm font-medium text-foreground">{d.question}</span>
      <span className="text-sm text-foreground/90">{d.decision}</span>
      {d.rationale.trim() && (
        <span className="text-[13px] text-muted-foreground">Why: {d.rationale}</span>
      )}
    </li>
  );
}

function Decisions({ epicId }: { epicId: string }) {
  const [all, setAll] = useState(false);
  const { data, isLoading, isError } = useDecisions({ epicId, limit: 100 });
  const decisions = data?.data ?? [];
  const shown = all ? decisions : decisions.slice(0, DECISIONS_SHOWN);
  return (
    <SectionCard
      title="Decisions"
      action={
        decisions.length > DECISIONS_SHOWN ? (
          <button
            type="button"
            onClick={() => {
              setAll((a) => !a);
            }}
            className="min-h-11 text-sm text-primary hover:underline"
          >
            {all ? "Show fewer" : `Show all ${String(decisions.length)}`}
          </button>
        ) : undefined
      }
    >
      {isLoading ? (
        <div className="h-16 animate-pulse rounded-lg bg-muted" />
      ) : isError ? (
        <p className="text-sm text-muted-foreground">Couldn't load the decisions.</p>
      ) : decisions.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No decisions written down yet. The Blueprint maker and Builder add them as they go.
        </p>
      ) : (
        <ul className="flex flex-col">
          {shown.map((d) => (
            <DecisionItem key={d.id} d={d} />
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function BlueprintPullRequests({
  epicId,
  buildJobId,
}: {
  epicId: string;
  buildJobId: string | undefined;
}) {
  const build = useJob(buildJobId);
  return (
    // Hidden while the list renders nothing (no pull requests and nothing to continue).
    <div className="overflow-hidden rounded-2xl border border-border bg-background empty:hidden [&>section]:border-t-0">
      <WaitingPullRequests epicId={epicId} {...(build.data ? { job: build.data } : {})} />
    </div>
  );
}

function Summary({ epic }: { epic: Epic }) {
  // The summary if there is one, else the long description.
  const text = [epic.structuredDesc?.summary.trim(), epic.description?.trim()].find(Boolean);
  return (
    <SectionCard title="What it builds">
      {text ? (
        <ShowMore long={text.length > LONG_TEXT}>
          <MarkdownRenderer content={text} className="text-[15px] text-foreground" />
        </ShowMore>
      ) : (
        <p className="text-sm text-muted-foreground">No summary written yet.</p>
      )}
    </SectionCard>
  );
}

export function BlueprintPage() {
  const { epicId = "" } = useParams<{ epicId: string }>();
  const { data: epic, isLoading, isError, refetch } = useBlueprint(epicId);
  const owner = useOwnerChecks(epicId);
  usePageTitle(epic?.name ?? "Blueprint");

  if (isLoading) {
    return (
      <div aria-busy="true" aria-label="Loading the blueprint" className="flex flex-col gap-4">
        <div className="h-10 w-2/3 animate-pulse rounded-xl bg-muted" />
        <div className="h-64 animate-pulse rounded-2xl bg-muted" />
      </div>
    );
  }
  if (isError || !epic) {
    return (
      <EmptyState
        title="Couldn't open this blueprint"
        body="It may have been removed, or you may not have access."
        action={
          <Link
            to="/blueprints"
            className="inline-flex min-h-11 items-center rounded-lg border border-border px-4 text-sm font-medium hover:bg-muted"
          >
            Back to Blueprints
          </Link>
        }
      />
    );
  }

  const result = blueprintStage(epic);
  const status = BLUEPRINT_STATUS[result.stage];
  const parts = epic.facts?.epic?.featureCount ?? epic._count?.features ?? 0;
  const partsDone = epic.facts?.epic?.completedFeatureCount ?? epic.completedFeatureCount ?? 0;
  // Only owner checks left: it's the owner's turn, not "Shipped".
  const ownerTurn = isOwnerTurn(
    result,
    !!(epic.facts?.buildJob ?? epic.facts?.planJob),
    { done: partsDone, total: parts, owner: owner.parts.length, checksLeft: owner.checksLeft }
  );
  const next: BlueprintNext = ownerTurn
    ? { title: ownerTurnTitle(owner.checksLeft), body: OWNER_TURN_BODY, to: null, action: null, urgent: true }
    : blueprintNextStep(epic, result);
  const progress =
    result.stage === "done" && !ownerTurn
      ? 100
      : parts > 0
        ? Math.round((partsDone / parts) * 100)
        : null;
  const idea = epic.convertedFromRequests?.[0] ?? null;
  const external = next.to?.startsWith("http") ?? false;

  return (
    <div className="flex flex-col gap-5">
      <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
        <Link to="/blueprints" className="hover:text-foreground">
          Blueprints
        </Link>
        <span aria-hidden="true"> › </span>
        <span>{epic.identifier ?? "Blueprint"}</span>
      </nav>

      <header className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <KindBadge kind="software" className="mt-1" />
          <h1 className="min-w-0 text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">
            {epic.name}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span
            className={cn(
              "inline-flex min-h-6 items-center rounded-full px-2.5 text-xs font-semibold",
              status.chip
            )}
          >
            {status.label}
          </span>
          {result.needsYou && (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
              {NEEDS_YOU_TEXT[result.needsYou]}
            </span>
          )}
          {ownerTurn && (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
              Your turn
            </span>
          )}
          <span>{epic.team?.name ?? "Personal"}</span>
          {epic.creator?.name && <span>· made by {epic.creator.name}</span>}
          {epic.updatedAt && <span>· {updatedAgo(epic.updatedAt)}</span>}
        </div>
      </header>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        <div className="flex min-w-0 flex-col gap-5">
          <Summary epic={epic} />
          <BlueprintPullRequests epicId={epic.id} buildJobId={epic.facts?.buildJob?.id} />
          <Parts epicId={epic.id} />
          <Decisions epicId={epic.id} />
        </div>

        <aside className="flex min-w-0 flex-col gap-4">
          <section
            aria-label="Next step"
            className={cn(
              "flex flex-col gap-2 rounded-2xl border p-4 sm:p-5",
              next.urgent
                ? "border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10"
                : "border-border bg-background"
            )}
          >
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Next
            </span>
            <h2 className="text-lg font-semibold text-foreground">{next.title}</h2>
            <p className="text-sm text-muted-foreground">{next.body}</p>
            {ownerTurn && <OwnerChecks parts={owner.parts} />}
            {next.decide && (
              <BuildDecision
                epicId={epic.id}
                onDecided={() => {
                  void refetch();
                }}
              />
            )}
            {next.build && !epic.isArchived ? (
              <div className={cn("mt-1", RUN_BUTTON)}>
                <RunOnMachineDialog
                  jobType="start_execution"
                  payload={{ kind: "orchestrate", epicId: epic.id }}
                  targetRemote={epic.team?.gitRemote ?? null}
                  defaultFastMode={epic.team?.fastMode ?? false}
                  defaultCheckMode={epic.team?.checkMode}
                  onStarted={() => {
                    void refetch();
                  }}
                >
                  <Laptop className="mr-2 h-4 w-4" aria-hidden="true" />
                  Start building
                </RunOnMachineDialog>
              </div>
            ) : (
              next.to &&
              next.action &&
              (external ? (
                <a
                  href={next.to}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  {next.action}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              ) : (
                <Link
                  to={next.to}
                  className={cn(
                    "mt-1 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    next.urgent
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "border border-border bg-background text-foreground hover:bg-muted"
                  )}
                >
                  {next.action}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              ))
            )}
          </section>

          <SectionCard title="At a glance">
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-muted-foreground">Parts done</dt>
                <dd className="text-lg font-semibold tabular-nums text-foreground">
                  {parts > 0 ? partsDoneText(partsDone, parts, owner.parts.length) : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Open pull requests</dt>
                <dd className="text-lg font-semibold tabular-nums text-foreground">
                  {epic.facts?.openPullRequests ?? 0}
                </dd>
              </div>
            </dl>
            {progress !== null && (
              <ProgressBar
                value={progress}
                label="Build progress"
                stage={result.stage}
                className="mt-4"
              />
            )}
          </SectionCard>

          <nav aria-label="More" className="flex flex-col gap-1 text-sm">
            {idea && (
              <Link
                to={`/ideas/${idea.id}`}
                className="min-h-11 content-center rounded-lg px-2 text-primary hover:bg-muted"
              >
                Open the idea{idea.identifier ? ` (${idea.identifier})` : ""}
              </Link>
            )}
            <Link
              to={`/epics/${epic.id}`}
              className="min-h-11 content-center rounded-lg px-2 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              Open the classic page
            </Link>
          </nav>
        </aside>
      </div>
    </div>
  );
}
